import { arc, pie, type PieArcDatum } from 'd3-shape';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTokenResultsByKeys } from '@/hooks/tokenQueries/useTokenResults';
import { useFiatDisplay } from '@/hooks/useFiatDisplay';

import { fontStyles } from '@/styles/typography';

import type { TokenAddressKey } from '@/utils/0x/token-registry.types';
import { classNames } from '@/utils/classnames';
import { varMappings } from '@/utils/generated/varMappings';
import { normalizeTokenAddress } from '@/utils/normalizeAddress';

import type { TokenBalance } from './types';

const SEGMENT_COLORS = [
  varMappings['token-purple-500'],
  varMappings['token-green-900'],
  varMappings['token-orange-500'],
  varMappings['token-red-600'],
  varMappings['token-purple-900'],
  varMappings['token-green-400'],
  varMappings['token-orange-900'],
  varMappings['token-red-400'],
  varMappings['token-purple-300'],
  varMappings['token-green-1000'],
];

const OTHERS_COLOR = varMappings['token-gray-500'];

interface AllocationItem {
  /** Stable identity for animation ordering (fungibleId or aggregation key) */
  key: string;
  symbol: string;
  name: string;
  value: number;
  percentage: number;
  color: string;
  logo?: string | null;
}

interface AllocationPieChartProps {
  tokens: TokenBalance[];
  totalValue: number;
  /** Size of the chart (width and height) */
  size?: number;
  /** Maximum number of tokens to show before grouping into "Others" */
  maxTokens?: number;
  isLoading?: boolean;
}

interface TooltipData {
  item: AllocationItem;
  x: number;
  y: number;
}

function formatPercent(value: number): string {
  if (value < 0.01) return '<0.01%';
  if (value < 1) return `${value.toFixed(2)}%`;
  return `${value.toFixed(1)}%`;
}

const ANIMATION_DURATION_MS = 400;

function easeInOutCubic(progress: number): number {
  return progress < 0.5
    ? 4 * progress * progress * progress
    : 1 - Math.pow(-2 * progress + 2, 3) / 2;
}

function lerp(from: number, to: number, progress: number): number {
  return from + (to - from) * progress;
}

type ArcAngles = { startAngle: number; endAngle: number };

/**
 * Smoothly interpolates pie arc angles when data changes.
 * Only animates when the segment count is unchanged and ordering is stable
 * (the same `item.key` values appear in the same positions). Otherwise snaps
 * directly to avoid misleading cross-segment transitions caused by
 * index-based interpolation.
 */
function useAnimatedArcs(pieData: PieArcDatum<AllocationItem>[]): PieArcDatum<AllocationItem>[] {
  const [animatedData, setAnimatedData] = useState(pieData);
  const currentAnglesRef = useRef<ArcAngles[]>([]);
  const prevKeysRef = useRef<string[]>([]);
  const rafIdRef = useRef(0);

  useEffect(() => {
    const targetAngles: ArcAngles[] = pieData.map((slice) => ({
      startAngle: slice.startAngle,
      endAngle: slice.endAngle,
    }));

    const fromAngles = currentAnglesRef.current;
    const currentKeys = pieData.map((slice) => slice.data.key);

    // Check if ordering changed — if so, snap to avoid misleading animations
    const orderingChanged =
      fromAngles.length !== pieData.length ||
      prevKeysRef.current.length !== currentKeys.length ||
      prevKeysRef.current.some((key, index) => key !== currentKeys[index]);

    prevKeysRef.current = currentKeys;

    if (fromAngles.length === 0 || pieData.length === 0 || orderingChanged) {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      currentAnglesRef.current = targetAngles;
      setAnimatedData(pieData);
      return;
    }

    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);

    const snapshot = fromAngles.map((angles) => ({ ...angles }));
    const startTime = performance.now();

    const tick = (now: number) => {
      const rawProgress = Math.min((now - startTime) / ANIMATION_DURATION_MS, 1);
      const easedProgress = easeInOutCubic(rawProgress);

      const interpolated: ArcAngles[] = pieData.map((_, index) => {
        const from = snapshot[index];
        const to = targetAngles[index];
        return {
          startAngle: lerp(from.startAngle, to.startAngle, easedProgress),
          endAngle: lerp(from.endAngle, to.endAngle, easedProgress),
        };
      });

      currentAnglesRef.current = interpolated;

      setAnimatedData(
        pieData.map((slice, index) => ({
          ...slice,
          startAngle: interpolated[index].startAngle,
          endAngle: interpolated[index].endAngle,
        })),
      );

      if (rawProgress < 1) {
        rafIdRef.current = requestAnimationFrame(tick);
      } else {
        currentAnglesRef.current = targetAngles;
        rafIdRef.current = 0;
      }
    };

    rafIdRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [pieData]);

  return animatedData;
}

export function AllocationPieChart({
  tokens,
  totalValue,
  size = 200,
  maxTokens = 8,
  isLoading,
}: AllocationPieChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipData | null>(null);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const { formatFiat } = useFiatDisplay();

  // Only fetch TokenResult for the top maxTokens segments (for prominentColor).
  const tokenKeys = useMemo<TokenAddressKey[]>(() => {
    if (!tokens.length) return [];

    const valueByKey = new Map<string, number>();
    const tokensByKey = new Map<string, TokenBalance[]>();

    for (const token of tokens) {
      if (token.balanceUsd == null || token.balanceUsd <= 0) continue;
      const key = token.fungibleId || token.symbol || token.tokenAddress;
      valueByKey.set(key, (valueByKey.get(key) ?? 0) + token.balanceUsd);
      const group = tokensByKey.get(key);
      if (group) group.push(token);
      else tokensByKey.set(key, [token]);
    }

    const topKeys = Array.from(valueByKey.entries())
      .sort(([, valueA], [, valueB]) => valueB - valueA)
      .slice(0, maxTokens)
      .map(([key]) => key);

    const keys: TokenAddressKey[] = [];
    for (const key of topKeys) {
      for (const token of tokensByKey.get(key)!) {
        if (token.tokenAddress && token.chainId) {
          keys.push({ address: token.tokenAddress, chainId: token.chainId });
        }
      }
    }

    return keys;
  }, [tokens, maxTokens]);

  const { tokenMap } = useTokenResultsByKeys(tokenKeys);

  const allocationData = useMemo((): AllocationItem[] => {
    if (!tokens.length || totalValue <= 0) return [];

    const aggregatedMap = new Map<
      string,
      {
        aggregationKey: string;
        symbol: string;
        name: string;
        value: number;
        logo?: string | null;
        prominentColor?: string | null;
      }
    >();

    for (const token of tokens) {
      if (token.balanceUsd == null || token.balanceUsd <= 0) continue;

      const aggregationKey = token.fungibleId
        ? token.fungibleId
        : token.symbol
          ? token.symbol
          : token.tokenAddress;
      const storedToken =
        tokenMap?.[token.chainId]?.[normalizeTokenAddress(token.tokenAddress, token.chainId)];

      const existing = aggregatedMap.get(aggregationKey);
      if (existing) {
        existing.value += token.balanceUsd;
        if (!existing.logo && token.logo) {
          existing.logo = token.logo;
        }
        if (!existing.prominentColor && storedToken?.prominentColor) {
          existing.prominentColor = storedToken.prominentColor;
        }
      } else {
        aggregatedMap.set(aggregationKey, {
          aggregationKey,
          symbol: token.symbol ?? 'Unknown',
          name: token.name ?? 'Unknown Token',
          value: token.balanceUsd,
          logo: token.logo,
          prominentColor: storedToken?.prominentColor,
        });
      }
    }

    const sortedTokens = Array.from(aggregatedMap.values()).sort(
      (tokenA, tokenB) => tokenB.value - tokenA.value,
    );

    const items: AllocationItem[] = [];
    let othersValue = 0;
    let othersCount = 0;

    sortedTokens.forEach((token, index) => {
      const percentage = (token.value / totalValue) * 100;

      if (index < maxTokens) {
        items.push({
          key: token.aggregationKey,
          symbol: token.symbol,
          name: token.name,
          value: token.value,
          percentage,
          color: token.prominentColor || SEGMENT_COLORS[index % SEGMENT_COLORS.length],
          logo: token.logo,
        });
      } else {
        othersValue += token.value;
        othersCount++;
      }
    });

    if (othersValue > 0) {
      const othersPercentage = (othersValue / totalValue) * 100;
      items.push({
        key: 'token-others',
        symbol: `Others (${othersCount})`,
        name: `${othersCount} other tokens`,
        value: othersValue,
        percentage: othersPercentage,
        color: OTHERS_COLOR,
      });
    }

    return items;
  }, [tokens, totalValue, maxTokens, tokenMap]);

  const [legendItems, setLegendItems] = useState(allocationData);
  const [legendOpacity, setLegendOpacity] = useState<0 | 1>(1);
  const legendRafRef = useRef(0);
  const isInitialLegendRef = useRef(true);

  useEffect(() => {
    if (isInitialLegendRef.current) {
      isInitialLegendRef.current = false;
      setLegendItems(allocationData);
      return;
    }

    if (legendRafRef.current) cancelAnimationFrame(legendRafRef.current);

    // Clear stale hover state so index/tooltip doesn't point at a wrong segment
    setHoveredIndex(null);
    setTooltip(null);

    // Update data immediately so indices stay in sync, only animate opacity
    setLegendItems(allocationData);
    setLegendOpacity(0);
    legendRafRef.current = requestAnimationFrame(() => {
      legendRafRef.current = 0;
      setLegendOpacity(1);
    });

    return () => {
      if (legendRafRef.current) cancelAnimationFrame(legendRafRef.current);
    };
  }, [allocationData]);

  const pieData = useMemo(() => {
    if (allocationData.length === 0) return [];

    const pieGenerator = pie<AllocationItem>()
      .value((slice) => slice.value)
      .sort(null); // Keep original order

    return pieGenerator(allocationData);
  }, [allocationData]);

  const animatedPieData = useAnimatedArcs(pieData);

  const arcGenerator = useMemo(() => {
    const outerRadius = size / 2 - 4;
    const innerRadius = outerRadius * 0.6; // Donut chart

    return arc<(typeof pieData)[0]>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius)
      .padAngle(0.02)
      .cornerRadius(3);
  }, [size]);

  const hoverArcGenerator = useMemo(() => {
    const outerRadius = size / 2;
    const innerRadius = (size / 2 - 4) * 0.6;

    return arc<(typeof pieData)[0]>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius)
      .padAngle(0.02)
      .cornerRadius(3);
  }, [size]);

  const tooltipRafRef = useRef(0);
  const pendingTooltipRef = useRef<{
    index: number;
    item: AllocationItem;
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    return () => {
      if (tooltipRafRef.current) cancelAnimationFrame(tooltipRafRef.current);
      pendingTooltipRef.current = null;
    };
  }, []);

  const handleSegmentMouseMove = useCallback(
    (event: React.MouseEvent<SVGPathElement>, index: number, item: AllocationItem) => {
      const containerRect = containerRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      pendingTooltipRef.current = {
        index,
        item,
        x: event.clientX - containerRect.left,
        y: event.clientY - containerRect.top,
      };

      if (!tooltipRafRef.current) {
        tooltipRafRef.current = requestAnimationFrame(() => {
          tooltipRafRef.current = 0;
          const pending = pendingTooltipRef.current;
          if (pending) {
            setHoveredIndex(pending.index);
            setTooltip({ item: pending.item, x: pending.x, y: pending.y });
          }
        });
      }
    },
    [],
  );

  const handleMouseLeave = useCallback(() => {
    if (tooltipRafRef.current) {
      cancelAnimationFrame(tooltipRafRef.current);
      tooltipRafRef.current = 0;
    }
    pendingTooltipRef.current = null;
    setHoveredIndex(null);
    setTooltip(null);
  }, []);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-4">
        <div
          style={{ width: size, height: size }}
          className="bg-token-alphalight-200 rounded-full animate-pulse"
        />
        <div className="flex flex-wrap gap-2 justify-center max-w-[200px]">
          {[1, 2, 3].map((index) => (
            <div key={index} className="h-4 w-16 bg-token-alphalight-200 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (allocationData.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4">
        <div
          style={{ width: size, height: size }}
          className="bg-token-alphalight-100 rounded-full flex items-center justify-center"
        >
          <span className={fontStyles({ variant: 'CAPTION', class: 'text-token-gray-600' })}>
            No data
          </span>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative flex flex-col h-full">
      <div className="flex-1 flex items-center justify-center relative">
        <svg width={size} height={size} className="overflow-visible">
          <g transform={`translate(${size / 2}, ${size / 2})`}>
            {animatedPieData.map((slice, index) => (
              <path
                key={slice.data.key}
                d={(hoveredIndex === index ? hoverArcGenerator(slice) : arcGenerator(slice)) ?? ''}
                fill={slice.data.color}
                className="transition-[fill,opacity] duration-150 cursor-default"
                opacity={hoveredIndex === null || hoveredIndex === index ? 1 : 0.6}
                onMouseMove={(event) => handleSegmentMouseMove(event, index, slice.data)}
                onMouseLeave={handleMouseLeave}
              />
            ))}
          </g>
        </svg>

        <div
          className="absolute flex flex-col items-center justify-center pointer-events-none"
          style={{
            width: size * 0.5,
            height: size * 0.5,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          {hoveredIndex !== null && allocationData[hoveredIndex] ? (
            <>
              <span
                className={fontStyles({
                  variant: 'LABEL_SM',
                  class: 'text-token-gray-1600 font-semibold text-center truncate max-w-full',
                })}
              >
                {allocationData[hoveredIndex].symbol}
              </span>
              <span
                className={fontStyles({
                  variant: 'CAPTION',
                  class: 'text-token-gray-700',
                })}
              >
                {formatPercent(allocationData[hoveredIndex].percentage)}
              </span>
            </>
          ) : (
            <span
              className={fontStyles({
                variant: 'CAPTION',
                class: 'text-token-gray-700',
              })}
            >
              Allocation
            </span>
          )}
        </div>
      </div>

      {tooltip && (
        <div
          className="absolute z-10 pointer-events-none bg-special-background-tooltip border border-token-gray-300 rounded-lg px-3 py-2 shadow-lg whitespace-nowrap"
          style={{
            left: tooltip.x,
            top: tooltip.y - 8,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className="flex items-center gap-2 mb-1">
            <div
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: tooltip.item.color }}
            />
            <span
              className={fontStyles({
                variant: 'LABEL_SM',
                class: 'text-token-gray-1600 font-medium',
              })}
            >
              {tooltip.item.symbol}
            </span>
          </div>
          <div className={fontStyles({ variant: 'CAPTION', class: 'text-token-gray-700' })}>
            {formatFiat(tooltip.item.value)} ({formatPercent(tooltip.item.percentage)})
          </div>
        </div>
      )}

      {/* fixed min-height so the chart doesn't shift when row count changes */}
      <div
        className="flex flex-wrap items-center content-end justify-center gap-x-2 gap-y-1.5 mt-4 min-h-[60px] transition-opacity duration-150 ease-out"
        style={{ opacity: legendOpacity }}
      >
        {legendItems.slice(0, 6).map((item, index) => (
          <div
            key={item.key}
            className={classNames(
              'flex items-center gap-2 cursor-default transition-opacity overflow-hidden',
              hoveredIndex !== null && hoveredIndex !== index ? 'opacity-50' : 'opacity-100',
            )}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
          >
            <div
              className="w-3 h-3 rounded-[4px] shrink-0"
              style={{ backgroundColor: item.color }}
            />
            <div className="flex gap-1 items-center">
              <span className={fontStyles({ variant: 'CAPTION', class: 'text-token-gray-1600' })}>
                {item.symbol}
              </span>
              <span className={fontStyles({ variant: 'CAPTION', class: 'text-token-gray-700' })}>
                {formatPercent(item.percentage)}
              </span>
            </div>
          </div>
        ))}
        {legendItems.length > 6 && (
          <span className={fontStyles({ variant: 'CAPTION', class: 'text-token-gray-700' })}>
            +{legendItems.length - 6} more
          </span>
        )}
      </div>
    </div>
  );
}

export function AllocationPieChartSkeleton({ size = 200 }: { size?: number }) {
  return (
    <div className="flex flex-col items-center gap-4">
      <div
        style={{ width: size, height: size }}
        className="bg-token-alphalight-200 rounded-full animate-pulse"
      />
      <div className="flex flex-wrap gap-2 justify-center max-w-[200px]">
        {[1, 2, 3].map((index) => (
          <div key={index} className="h-4 w-16 bg-token-alphalight-200 rounded animate-pulse" />
        ))}
      </div>
    </div>
  );
}
